import { useRef, useState } from "react";

const SAVE_EVERY_SECONDS = 5;

interface Options {
  start?: number;
  /** Playback pauses here. */
  end?: number;
  onProgress: (seconds: number) => void;
}

/** <video> behaviour of the player: start, stop at end, progress, play state, source swaps. */
const useVideoPlayer = ({ start = 0, end, onProgress }: Options) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [time, setTime] = useState(start);
  const [paused, setPaused] = useState(true);
  const stoppedAtEnd = useRef(false);
  const lastSaved = useRef(start);
  // Set before the source changes so the new file resumes where the old one was.
  const resume = useRef<{ time: number; play: boolean } | null>(null);

  const seek = (seconds: number): void => {
    const element = ref.current;
    if (!element) return;
    stoppedAtEnd.current = true; // an explicit seek lifts the end stop
    element.currentTime = seconds;
    void element.play().catch(() => undefined);
  };

  const toggle = (): void => {
    const element = ref.current;
    if (!element) return;
    if (element.paused) void element.play().catch(() => undefined);
    else element.pause();
  };

  /** Call right before changing the source: the next file keeps the position and play state. */
  const keepPosition = (): void => {
    const element = ref.current;
    if (element) resume.current = { time: element.currentTime, play: !element.paused };
  };

  const handlers = {
    onLoadedMetadata: () => {
      const element = ref.current;
      if (!element) return;
      const pending = resume.current;
      resume.current = null;
      element.currentTime = pending ? pending.time : start;
      if (!pending || pending.play) void element.play().catch(() => undefined);
    },
    onPlay: () => setPaused(false),
    onTimeUpdate: () => {
      const element = ref.current;
      if (!element) return;
      setTime(element.currentTime);
      if (!stoppedAtEnd.current && end !== undefined && element.currentTime >= end) {
        stoppedAtEnd.current = true;
        element.pause();
      }
      if (Math.abs(element.currentTime - lastSaved.current) >= SAVE_EVERY_SECONDS) {
        lastSaved.current = element.currentTime;
        onProgress(element.currentTime);
      }
    },
    onPause: () => {
      setPaused(true);
      onProgress(ref.current?.currentTime ?? time);
    },
    onEnded: () => onProgress(ref.current?.currentTime ?? time),
  };

  return { ref, time, paused, seek, toggle, keepPosition, handlers, currentTime: () => ref.current?.currentTime ?? time };
};

export default useVideoPlayer;
